import { memo, useMemo } from 'react'
import classNames from 'classnames'
import {
  IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT,
  IMAGE_ASSET_PATH_FILTER_RESIZE_AND_PAD
} from '@aytm/lib/common/assets'

import { imageAsset as imageAssetUrl } from '@aytm/app/survey/editor/resources/urls'

import { TableDataViewCol } from '@aytm/app/stats/page/question-views/components/table/types'

import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import ImagePreview from '@aytm/app/survey/editor/components/content/assets/ImagePreview'

// styles
import style from '@aytm/app/stats/page/question-views/components/table/styles/Table.module.scss'

const TITLE_IMAGE_THUMB_MIN = 70
const TITLE_IMAGE_THUMB_MAX = 265

const TITLE_THUMB_WIDTH = 23
const TITLE_THUMB_HEIGHT = 23

type TableHeaderProps = {
  index?: number
  cols: TableDataViewCol[]
  onSort: (colIndex: number, dir: 1 | -1) => void
}

type StylesProps = {
  flexGrow?: number
  flexShrink?: number
  flexBasis?: string
  width?: string
}

const getStyles = (cell: TableDataViewCol) => {
  const values: StylesProps = {}
  if ('width' in cell && Number.isFinite(cell.width)) {
    values.flexGrow = 0
    values.flexShrink = 0
    values.flexBasis = `${cell.width}px`
    values.width = `${cell.width}px`
  }
  if ('width' in cell && 'string' == typeof cell.width) {
    values.flexGrow = 0
    values.flexShrink = 0
    values.flexBasis = cell.width
    values.width = cell.width
  }
  return values
}

export default memo(TableHeader)
export function TableHeader(props: TableHeaderProps) {
  const cols = useMemo(
    () =>
      props.cols.some(col => 'cols' in col)
        ? props.cols.reduce((acc: TableDataViewCol[], cur: TableDataViewCol) => {
            const col: TableDataViewCol = { type: cur.type, title: '' }
            return acc.concat('cols' in cur ? cur.cols : [col])
          }, [])
        : [],
    [props.cols]
  )

  return (
    <>
      <div className={style.header}>
        {props.cols.map((cell, index) => (
          <div
            key={`table-header-cell-${index + 1}`}
            className={classNames(style['header-cell'], style[`header-cell-${cell.type}`], {
              [style['header-cell-active']]: 'sort' in cell || 'onClick' in cell
            })}
            style={getStyles(cell)}
            title={'tooltip' in cell ? cell.tooltip : null}
            onClick={
              'sort' in cell
                ? () => props.onSort(props.index + index, 'sorted' in cell ? (1 == cell.sorted ? -1 : 1) : 1)
                : 'onClick' in cell
                  ? cell.onClick
                  : null
            }
          >
            {'image' in cell ? (
              <div className={style['header-cell-thumbnail']}>
                <Popup
                  portal
                  trigger="hover"
                  direction="left"
                  content={() => (
                    <ImagePreview
                      minWidth={`${TITLE_IMAGE_THUMB_MIN}px`}
                      maxWidth={`${TITLE_IMAGE_THUMB_MAX}px`}
                      minHeight={`${TITLE_IMAGE_THUMB_MIN}px`}
                      maxHeight={`${TITLE_IMAGE_THUMB_MAX}px`}
                      imageUrl={cell.image.original.url}
                      previewUrl={imageAssetUrl(cell.image.original.path, {
                        width: TITLE_IMAGE_THUMB_MAX,
                        height: TITLE_IMAGE_THUMB_MAX,
                        filter: IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT,
                        unsharp: true,
                        backgroundColor: 'transparent'
                      })}
                    />
                  )}
                  contentTheme="thin-black"
                  hideOtherPopupsOnShow
                >
                  <img
                    src={imageAssetUrl(cell.image.original.path, {
                      width: TITLE_THUMB_WIDTH,
                      height: TITLE_THUMB_HEIGHT,
                      filter: IMAGE_ASSET_PATH_FILTER_RESIZE_AND_PAD,
                      unsharp: true,
                      backgroundColor: 'white'
                    })}
                  />
                </Popup>
              </div>
            ) : null}
            <span className={style['header-cell-text']}>
              <span dangerouslySetInnerHTML={{ __html: cell.title }} />
              {'sort' in cell ? (
                <span
                  className={classNames(style['header-cell-sort'], {
                    [style['header-cell-sorted']]: 'sorted' in cell,
                    [style['header-cell-sorted-asc']]: 'sorted' in cell && 1 == cell.sorted,
                    [style['header-cell-sorted-desc']]: 'sorted' in cell && -1 == cell.sorted
                  })}
                ></span>
              ) : null}
            </span>
            {'comments' in cell ? (
              <div
                className={classNames(style['header-cell-comments'], {
                  [style['header-cell-comments-disabled']]: 0 == cell.comments
                })}
                onClick={
                  0 < cell.comments && 'onCommentsClick' in cell ? event => cell.onCommentsClick(event, -1) : null
                }
              >
                <span className={style['header-cell-comments-count']}>{cell.comments}</span>
                <span className={style['header-cell-comments-icon']} title={`View ${cell.comments} custom answers`}>
                  <i className="icon-aytmfd-comments-v2" />
                </span>
              </div>
            ) : null}
            {'appendix' in cell ? (
              <span className={style['header-cell-appendix']} dangerouslySetInnerHTML={{ __html: cell.appendix }} />
            ) : null}
          </div>
        ))}
      </div>

      {0 < cols.length ? (
        <TableHeader index={props.index + props.cols.length} cols={cols} onSort={props.onSort} />
      ) : null}
    </>
  )
}
